// Sentry Error Monitoring Configuration for VALORAMAP Frontend
import * as Sentry from "@sentry/react";

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN || '';
const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || 'development';
const APP_VERSION = process.env.REACT_APP_VERSION || '1.0.0';

/**
 * Initialize Sentry for React frontend
 */
export function initSentry() {
  if (!SENTRY_DSN) {
    console.warn('SENTRY_DSN not configured - error monitoring disabled');
    return false;
  }

  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: ENVIRONMENT,
      release: `valoramap@${APP_VERSION}`,
      
      // Integrations
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      
      // Sample rates - adjust for production
      tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      
      // Debugging
      attachStacktrace: true,
      maxBreadcrumbs: 50,
      
      // Filter events before sending
      beforeSend(event, hint) {
        // Filter out browser extension errors
        if (event.exception) {
          const error = hint.originalException;
          const message = (error?.message || '').toLowerCase();
          
          // Skip common noise
          if (
            message.includes('network') ||
            message.includes('adblock') ||
            message.includes('extension') ||
            message.includes('script error') ||
            message.includes('resizeobserver')
          ) {
            return null;
          }
        }
        
        // In production, skip 4xx errors
        if (ENVIRONMENT === 'production') {
          const statusCode = event.contexts?.response?.status_code;
          if (statusCode >= 400 && statusCode < 500) {
            return null;
          }
        }
        
        return event;
      },
      
      // Filter breadcrumbs
      beforeBreadcrumb(breadcrumb) {
        // Never log auth-related breadcrumbs
        if (breadcrumb.category === 'auth') {
          return null;
        }
        
        // Skip storage events with tokens
        if (breadcrumb.category === 'storage' && breadcrumb.message?.includes('token')) {
          return null;
        }
        
        return breadcrumb;
      },
    });

    console.log(`Sentry initialized for environment: ${ENVIRONMENT}`);
    return true;
    
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
    return false;
  }
}

/**
 * Capture an error with optional context
 */
export function captureError(error, context = {}) {
  if (!SENTRY_DSN) {
    console.error('Error (Sentry disabled):', error);
    return;
  }

  Sentry.withScope((scope) => {
    if (context.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }
    
    if (context.extra) {
      scope.setContext('error_context', context.extra);
    }
    
    if (context.user) {
      scope.setUser(context.user);
    }
    
    Sentry.captureException(error);
  });
}

/**
 * Capture an informational message
 */
export function captureMessage(message, level = 'info', tags = {}) {
  if (!SENTRY_DSN) {
    console.log(`Message (Sentry disabled): ${message}`);
    return;
  }

  Sentry.withScope((scope) => {
    Object.entries(tags).forEach(([key, value]) => {
      scope.setTag(key, value);
    });
    
    Sentry.captureMessage(message, level);
  });
}

/**
 * Add a breadcrumb for debugging context
 */
export function addBreadcrumb(category, message, data = {}, level = 'info') {
  if (!SENTRY_DSN) return;
  
  Sentry.addBreadcrumb({
    category,
    message,
    level,
    data,
  });
}

/**
 * Set user context for errors
 */
export function setUser(userId, email = null, username = null) {
  if (!SENTRY_DSN) return;
  
  Sentry.setUser({
    id: userId,
    email,
    username,
  });
}

/**
 * Clear user context
 */
export function clearUser() {
  if (!SENTRY_DSN) return;
  
  Sentry.setUser(null);
}

/**
 * Error Boundary wrapper component
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

/**
 * Higher-order component to wrap components with error boundary
 */
export const withErrorBoundary = Sentry.withErrorBoundary;

export default Sentry;
